import React, { useCallback, useMemo } from "react";
import { useNavigate, generatePath } from "react-router-dom";
import { useListTrapSitesQuery } from "../../../actions/trapsiteInQuery";
import { useListDevicesQuery } from "../../../actions/devicesInQuery";
import { useInfiniteAlerts } from "../../../hooks/useInfiniteAlerts";
import { useUpdateAlertStatusMutation } from "../../../actions/alertsInQuery";
import { ROUTES } from "../../../config/routes";
import { getAlertMeshCaptureId } from "../../../utils/helpers";
import { MaintenanceAlert } from "./MaintenanceAlert";
import "./index.css";

const Index: React.FC = () => {
  const navigate = useNavigate();
  const { data: trapSitesData, isLoading: isTrapSiteLoading } =
    useListTrapSitesQuery();
  const { data: devices, isLoading: isDevicesLoading } = useListDevicesQuery();

  const {
    allAlerts,
    isLoading: isAlertsLoading,
    isFetchingMore,
    handleScroll,
  } = useInfiniteAlerts({
    types: ["maintenance"],
    pollingInterval: 60000,
  });

  const [updateAlertStatus] = useUpdateAlertStatusMutation();

  // Only a mesh_dirty alert that names its capture leads anywhere
  const handleClickAlert = useCallback(
    (id: string) => {
      const alert = allAlerts.find((a) => a._id === id);
      const captureId = alert && getAlertMeshCaptureId(alert);
      if (!captureId) return;
      if (alert.status === "unread") {
        updateAlertStatus({ id, status: "read" });
      }
      navigate(generatePath(ROUTES.mesh_detail, { id: captureId }));
    },
    [allAlerts, navigate, updateAlertStatus],
  );

  const joinedAlerts = useMemo(() => {
    if (!allAlerts.length) {
      return [];
    }

    return allAlerts.map((alert) => {
      const device = devices?.items?.find(
        (d) => d.uuid === alert.context.device,
      );

      const trapSite = trapSitesData?.items?.find((t) => {
        return t._id === (alert.context?.trap_site_id || device?.trap_site_id);
      });

      return {
        ...alert,
        device_name: device?.display_name || "",
        trap_name: trapSite?.display_name || "",
      };
    });
  }, [allAlerts, devices, trapSitesData]);

  return (
    <MaintenanceAlert
      loading={isTrapSiteLoading || isDevicesLoading || isAlertsLoading}
      isFetchingMore={isFetchingMore}
      joinedAlerts={joinedAlerts}
      onScroll={handleScroll}
      onAlertClick={handleClickAlert}
    />
  );
};

export default Index;
